import React, { useState, useEffect, useRef } from "react";
import CryptoRecommendationMatrix from "./components/CryptoRecommendationMatrix";
import { apiService } from "./services/api";
import { InvestorPersona, CryptoAsset, PersonaRecommendations } from "./types";

function App() {
  const [personas, setPersonas] = useState<InvestorPersona[]>([]);
  const [assets, setAssets] = useState<CryptoAsset[]>([]);
  const [recommendations, setRecommendations] = useState<
    Record<string, PersonaRecommendations>
  >({});
  const [loading, setLoading] = useState(false);
  const [expandedRowsLoading, setExpandedRowsLoading] = useState<Set<string>>(
    new Set()
  );
  const [generatedAssets, setGeneratedAssets] = useState<Set<string>>(
    new Set()
  );
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    // Prevent duplicate calls (React StrictMode causes useEffect to run twice in development)
    if (loadingRef.current || hasLoadedInitialData) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      setHasLoadedInitialData(true);

      const [personasData, assetsData] = await Promise.all([
        apiService.getPersonas(),
        apiService.getCryptoAssets(),
      ]);

      setPersonas(personasData);
      setAssets(assetsData); // Show all 100 crypto assets
      setIsInitialLoad(false);

      // Clear any existing recommendations - don't fetch them on initial load
      setRecommendations({});
    } catch (err) {
      setError("Failed to load initial data. Please try again.");
      console.error("Error loading initial data:", err);
      setIsInitialLoad(false);
      setHasLoadedInitialData(false); // Reset on error to allow retry
      loadingRef.current = false; // Reset ref on error to allow retry
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendationsForAsset = async (assetSymbol: string) => {
    if (personas.length === 0 || assets.length === 0) {
      setError("Please wait for personas and assets to load first.");
      return;
    }

    // Check if we already have generated recommendations for this asset
    if (generatedAssets.has(assetSymbol)) {
      return; // Already loaded
    }

    try {
      setExpandedRowsLoading((prev) => new Set(prev).add(assetSymbol));
      setError(null);

      const targetAsset = assets.find((asset) => asset.symbol === assetSymbol);
      if (!targetAsset) {
        throw new Error(`Asset ${assetSymbol} not found`);
      }

      const assetSymbols = [assetSymbol];
      const batchSize = 3;
      const newRecommendations: Record<string, PersonaRecommendations> = {};

      for (let i = 0; i < personas.length; i += batchSize) {
        const batch = personas.slice(i, i + batchSize);

        const batchPromises = batch.map(async (persona) => {
          try {
            console.log(
              `Generating recommendations for ${persona.name} - ${assetSymbol}...`
            );
            const personaRecs = await apiService.getRecommendations(
              persona.id,
              assetSymbols
            );
            console.log(
              `SUCCESS: Got recommendations for ${persona.name} - ${assetSymbol}:`,
              personaRecs
            );
            return { personaId: persona.id, recommendations: personaRecs };
          } catch (error: any) {
            console.error(
              `Failed to get recommendations for ${persona.name} - ${assetSymbol}:`,
              error
            );
            return {
              personaId: persona.id,
              recommendations: {
                personaId: persona.id,
                recommendations: [
                  {
                    asset: targetAsset,
                    action: "HOLD" as const,
                    confidence: 0.5,
                    reasoning: `Unable to generate recommendation: ${
                      error.message || "Timeout or server error"
                    }`,
                    timeframe: "N/A",
                    riskAssessment: "Unable to assess",
                    volatilityAnalysis: {
                      historicalVol30d: 0.5,
                      impliedVol: 0.6,
                      volRiskScore: "Medium" as const,
                      blackScholesRange: {
                        lower: targetAsset.currentPrice * 0.8,
                        upper: targetAsset.currentPrice * 1.2,
                      },
                    },
                  },
                ],
                marketSummary:
                  "Unable to generate market summary due to API timeout.",
                personalizedAdvice:
                  "Please try refreshing recommendations later.",
                timestamp: new Date(),
              },
            };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach((result) => {
          if (result) {
            // Merge new recommendations with existing ones for this persona
            const existingPersonaRecs = recommendations[result.personaId];
            if (existingPersonaRecs) {
              // Create a set of existing asset symbols to prevent duplicates
              const existingAssetSymbols = new Set(
                existingPersonaRecs.recommendations.map(rec => rec.asset.symbol)
              );
              
              // Filter out any new recommendations for assets that already exist
              const newRecommendationsFiltered = result.recommendations.recommendations.filter(
                rec => !existingAssetSymbols.has(rec.asset.symbol)
              );
              
              // Combine existing recommendations with filtered new ones
              newRecommendations[result.personaId] = {
                ...result.recommendations,
                recommendations: [
                  ...existingPersonaRecs.recommendations,
                  ...newRecommendationsFiltered
                ]
              };
            } else {
              // No existing recommendations for this persona
              newRecommendations[result.personaId] = result.recommendations;
            }
          }
        });

        if (i + batchSize < personas.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      setRecommendations((prev) => ({ ...prev, ...newRecommendations }));
      setGeneratedAssets((prev) => new Set(prev).add(assetSymbol));
    } catch (err) {
      setError(
        `Failed to generate recommendations for ${assetSymbol}. Please try again.`
      );
      console.error(
        `Error generating recommendations for ${assetSymbol}:`,
        err
      );
    } finally {
      setExpandedRowsLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(assetSymbol);
        return newSet;
      });
    }
  };

  if (isInitialLoad) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Loading crypto data...
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            AI recommendations will be generated when you expand specific assets
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-8 px-3 sm:px-6 lg:px-8 pb-24">
        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-sm font-medium text-red-800 hover:text-red-700"
                    onClick={() => setError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div className="text-left mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center">
            <img
              src="/logo.svg"
              alt="Logo"
              className="w-8 h-8 sm:w-12 sm:h-12 mr-2 sm:mr-3"
            />
            <span>Agentic Crypto</span>
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
            Advanced AI-powered crypto portfolio construction with multi-agent systems for dynamic optimization and personalized investment strategies
          </p>

          {personas.length > 0 && (
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
              Click on any asset row to generate AI recommendations from{" "}
              {personas.length} investor personas
            </p>
          )}
        </div>

        {/* Main Table */}
        {assets.length > 0 && personas.length > 0 ? (
          <CryptoRecommendationMatrix
            assets={assets}
            personas={personas}
            recommendations={recommendations}
            loading={loading}
            expandedRowsLoading={expandedRowsLoading}
            generatedAssets={generatedAssets}
            onRowExpand={loadRecommendationsForAsset}
          />
        ) : (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">
              Loading crypto assets and investor personas...
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-3 sm:py-4 px-3 sm:px-6 lg:px-8">
          <div className="text-center text-xs sm:text-sm text-gray-500">
            <p className="flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-2">
              <span>
                By{" "}
                <a
                  href="https://emdata.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  emdata.ai
                </a>
              </span>
              <span className="hidden sm:inline">•</span>
              <span>Not investment advice</span>
              <span className="hidden sm:inline">•</span>
              <a
                href="mailto:support@emdata.ai?subject=Agentic Crypto"
                className="hover:underline"
              >
                Contact
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
